import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { JhiEventManager, JhiLanguageService } from '../../shared/core-utils';
import { AlertService } from '../../shared/alert/alert.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { formatErrorList } from '../../shared/alert/format-error-list';
import { Subscription } from 'rxjs';
import { SubscriptionsManager } from '../../shared/util/subscriptions-manager';
import { ColumnCategory, ColumnModel } from '../../shared/query/model/column.model';
import { HeadToHeadQueryContext } from '../head-to-head-query-context';
import { HeadToHeadQueryService } from '../head-to-head-query.service';
import { Weight } from '../../shared/query/model/weight.model';
import { GermplasmPairTraitModel } from '../model/germplasm-pair-trait.model';
import { TrialEnvironmentSearchResponse } from '../../shared/query/model/trial-environment-search-response.model';
import { TrialEnvironmentSearchRequest } from '../../shared/query/model/trial-environment-search-request.model';
import { SORT_PREDICATE_NONE } from '../../germplasm-manager/germplasm-search-resolve-paging-params';

@Component({
    selector: 'jhi-select-environments-step',
    styleUrls: ['./select-environments-step.component.scss'],
    templateUrl: './select-environments-step.component.html'
})
export class SelectEnvironmentsStepComponent implements OnInit {
    subscriptionManager = new SubscriptionsManager();
    @Output() next = new EventEmitter<void>();
    @Output() back = new EventEmitter<void>();
    @Input() active = false;
    environmentWeightOptions = [
        { label: 'head-to-head-query.environment.weight.important', value: Weight.IMPORTANT },
        { label: 'head-to-head-query.environment.weight.critical', value: Weight.CRITICAL },
        { label: 'head-to-head-query.environment.weight.desirable', value: Weight.DESIRABLE },
        { label: 'head-to-head-query.environment.weight.ignored', value: Weight.IGNORED }
    ];
    traitEnvironments: TrialEnvironmentSearchResponse[];
    originalList: TrialEnvironmentSearchResponse[];
    pagedData: TrialEnvironmentSearchResponse[];
    environmentDetailColumns: ColumnModel[];
    environmentHiddenColumns = {};
    selectedTraitModels: GermplasmPairTraitModel[] = [];
    selectedTraits: number[] = [];
    environmentIds: number[] = [];
    locationFilter: number[] = [];
    environmentConditions: number[] = [];
    studyFilter: number[] = [];
    isLoading: boolean;
    selectedItems: Map<number, TrialEnvironmentSearchResponse> = new Map<number, TrialEnvironmentSearchResponse>();
    lastClickIndex: any;
    totalItems: number;
    itemsPerPage: number = 20;
    page: number;
    previousPage: number;
    predicate: any;
    isAscending: boolean;
    isSelectAll: boolean;
    eventSubscriber: Subscription;
    protected readonly Weight = Weight;

    protected readonly staticColumns = [
        new ColumnModel(-1, 'head-to-head-query.column.header.location', ColumnCategory.STATIC, true, null, true),
        new ColumnModel(-2, 'head-to-head-query.column.header.country', ColumnCategory.STATIC, true, null, true),
        new ColumnModel(-3, 'head-to-head-query.column.header.study', ColumnCategory.STATIC, true, null, true),
        new ColumnModel(-4, 'head-to-head-query.column.header.weight', ColumnCategory.STATIC, true, null, true)
    ];

    constructor(private activatedRoute: ActivatedRoute,
                private eventManager: JhiEventManager,
                private jhiLanguageService: JhiLanguageService,
                private alertService: AlertService,
                public queryContext: HeadToHeadQueryContext,
                private headToHeadQueryService: HeadToHeadQueryService,
                private router: Router) {
        this.selectedTraits = [];
    }

    get hiddenColumns() {
        return this.environmentHiddenColumns;
    }

    set hiddenColumns(hiddenColumns) {
        this.environmentHiddenColumns = hiddenColumns;
    }

    ngOnInit(): void {
        this.page = 1;
        this.previousPage = 1;
        this.totalItems = 0;
        this.isSelectAll = false;
        this.clearSort();
        this.traitEnvironments = [];
        this.originalList = [];
        this.environmentDetailColumns = [];
        this.registerEnvironmentFilterChange();
    }

    registerEnvironmentFilterChange() {
        this.eventSubscriber = this.eventManager.subscribe('environmentFilterViewChanged', (event) => {
            if (event.content.locationFilter) {
                this.locationFilter = event.content.locationFilter;
            }
            if (event.content.studyFilter) {
                this.studyFilter = event.content.studyFilter;
            }
            this.loadAll();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['active'] && this.active) {
            this.traitEnvironments = [];
            this.originalList = [];
            this.page = 1;
            this.previousPage = 1;
            if (this.queryContext.traits && this.queryContext.traits.length) {
                this.environmentIds = [];
                this.environmentConditions = [];
                this.environmentDetailColumns = [];
                this.selectedTraits = [];
                this.selectedTraitModels = this.queryContext.traits;
                this.queryContext.traits.forEach((trait) => {
                    this.selectedTraits.push(trait.variableId);
                    this.environmentIds = [...new Set([...this.environmentIds, ...trait.environmentIds])];
                });
            }
            this.loadAll();
        }
    }

    updatePagedData() {
        if (this.traitEnvironments && this.traitEnvironments.length > 0) {
            // Calculate starting and ending indices for the current page
            const startIndex = (this.page - 1) * this.itemsPerPage;
            const endIndex = Math.min(Number(startIndex) + Number(this.itemsPerPage), this.totalItems);

            // Slice the data to get the current page data
            this.pagedData = this.traitEnvironments.slice(startIndex, endIndex);
        } else {
            this.pagedData = [];
            this.totalItems = 0;
        }
        this.isLoading = false;
    }

    openLocationFilterModal() {
        this.router.navigate(['/', { outlets: { popup: 'environments-location-filter-dialog' }, }], {
            replaceUrl: true,
            queryParamsHandling: 'merge',
            queryParams: {
                selectedTraits: this.selectedTraits,
                locationFilter: this.locationFilter
            }
        });
    }

    openStudyFilterModal() {
        this.router.navigate(['/', { outlets: { popup: 'environments-study-filter-dialog' }, }], {
            replaceUrl: true,
            queryParamsHandling: 'merge',
            queryParams: {
                selectedTraits: this.selectedTraits,
                studyFilter: this.studyFilter,
                environmentIds: this.environmentIds
            }
        });
    }

    isPageSelected() {
        return this.size() && this.pagedData.every((entry: TrialEnvironmentSearchResponse) => Boolean(this.selectedItems.get(entry.id)));
    }

    size() {
        return this.isSelectAll ? this.totalItems : this.selectedItems.size;
    }

    onSelectPage() {
        if (this.isPageSelected()) {
            // remove all items
            this.pagedData.forEach((entry) => this.selectedItems.delete(entry.id));
        } else {
            // check remaining items
            this.pagedData.forEach((entry) => {
                entry.weight = Weight.IMPORTANT;
                this.selectedItems.set(entry.id, entry);
            });
        }
    }

    onSelectAll(isSelectAll) {
        this.isSelectAll = !isSelectAll;
        this.clearSelectedItems();
        if (this.isSelectAll) {
            this.traitEnvironments.forEach((item: TrialEnvironmentSearchResponse) => {
                item.weight = Weight.IMPORTANT;
                this.selectedItems.set(item.id, item);
            });
        }
    }

    clearSelectedItems() {
        this.selectedItems.clear();
    }

    onClearSort($event) {
        $event.preventDefault();
        this.clearSort();
        this.traitEnvironments = [...this.originalList];
        this.updatePagedData();
    }

    toggleSelect($event, index, entry: TrialEnvironmentSearchResponse, checkbox = false) {
        if (this.isSelectAll) {
            return;
        }
        if (!$event.ctrlKey && !checkbox) {
            this.clearSelectedItems();
        }
        let items;
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1,
                min = Math.min(this.lastClickIndex, index);
            items = this.traitEnvironments.slice(min, max);
        } else {
            items = [entry];
            this.lastClickIndex = index;
        }
        const isClickedItemSelected = this.selectedItems.has(entry.id);
        for (const item of items) {
            if (isClickedItemSelected) {
                this.selectedItems.delete(item.id);
            } else {
                item.weight = Weight.IMPORTANT;
                this.selectedItems.set(item.id, item);
            }
        }
    }

    isSelected(entry: TrialEnvironmentSearchResponse) {
        return this.selectedItems.get(entry.id);
    }

    loadAll() {
        this.isLoading = true;
        const request = this.createRequest();
        this.subscriptionManager.add = this.headToHeadQueryService.getSearchResults(request
        ).subscribe(
            (res: HttpResponse<TrialEnvironmentSearchResponse[]>) => this.onSuccess(res)
        );
    }

    private createRequest() {
        const request = new TrialEnvironmentSearchRequest();
        request.studyFilter = this.studyFilter;
        request.locationFilter = this.locationFilter;
        request.environmentIds = this.environmentIds;
        request.traits = this.selectedTraits;
        request.envConditions = this.environmentConditions;
        request.germplasmPairs = this.queryContext.germplasmPairs;
        return request;
    }

    loadPage(page: number) {
        if (page !== this.previousPage) {
            this.previousPage = page;
            this.updatePagedData();
        }
    }

    sort() {
        this.traitEnvironments = [...this.originalList];
        if (this.predicate === 'LOCATION') {
            this.traitEnvironments.sort((a, b) => {
                if (this.isAscending) {
                    return a.locationName > b.locationName ? 1 : -1;
                } else {
                    return a.locationName < b.locationName ? 1 : -1;
                }
            });
        } else if (this.predicate === 'COUNTRY') {
            this.traitEnvironments.sort((a, b) => {
                if (a.isoAbbr === undefined && b.isoAbbr === undefined) {
                    return 0;
                }
                if (a.isoAbbr === undefined) {
                    return 1;
                }
                if (b.isoAbbr === undefined) {
                    return -1;
                }

                if (this.isAscending) {
                    return a.isoAbbr > b.isoAbbr ? 1 : -1;
                } else {
                    return a.isoAbbr < b.isoAbbr ? 1 : -1;
                }
            });
        } else if (this.predicate === 'STUDY') {
            this.traitEnvironments.sort((a, b) => {
                if (this.isAscending) {
                    return a.studyName > b.studyName ? 1 : -1;
                } else {
                    return a.studyName < b.studyName ? 1 : -1;
                }
            });
        } else {
            this.traitEnvironments.sort((a, b) => {
                if (this.isAscending) {
                    return a.variables[this.predicate] > b.variables[this.predicate] ? 1 : -1;
                } else {
                    return a.variables[this.predicate] < b.variables[this.predicate] ? 1 : -1;
                }
            });
        }
        this.updatePagedData();
    }

    resetTable() {
        this.page = 1;
        this.previousPage = 1;
        this.isSelectAll = false;
        this.clearSelectedItems();
        this.updatePagedData();
    }

    trackId(index: number, item: any) {
        return item.id;
    }

    onColumnsSelected(columns: ColumnModel[]) {
        this.hiddenColumns = this.getHiddenColumns(columns);
        this.environmentDetailColumns = [];
        this.environmentConditions = [];
        columns.forEach(column => {
            if (column.id > 0 && column.selected && !this.selectedTraits.includes(column.id)) {
                this.environmentDetailColumns.push(column);
                this.environmentConditions.push(column.id);
            }
        });
        if (this.environmentConditions.length > 0) {
            this.traitEnvironments = [];
            this.clearSort();
            this.loadAll();
        } else {
            this.updatePagedData();
        }
    }

    selectedWeight(traitEnvironment: TrialEnvironmentSearchResponse) {
        const traitEnv = this.isSelected(traitEnvironment);
        if (traitEnv) {
            traitEnv.weight = traitEnvironment.weight;
        }
    }

    hasFilterApplied() {
        return (this.locationFilter.length > 0 || this.studyFilter.length > 0);
    }

    colspan() {
        const countEnvCon = this.environmentDetailColumns.length;
        return this.isSelectAll ? countEnvCon + 5 : countEnvCon + 6;
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
        this.eventSubscriber.unsubscribe();
    }

    private clearSort() {
        this.predicate = SORT_PREDICATE_NONE;
        this.isAscending = true;
    }

    private onSuccess(data) {
        this.traitEnvironments = data.body;
        this.originalList = [...this.traitEnvironments];
        this.totalItems = this.traitEnvironments.length;
        this.traitEnvironments.forEach(traitEnvironment => {
            traitEnvironment.weight = this.selectedItems.has(traitEnvironment.id) ?
                this.selectedItems.get(traitEnvironment.id).weight : Weight.IGNORED;
        });
        this.updatePagedData();
    }

    private getHiddenColumns(selectedColumns): ColumnModel[] {
        const unselectedColumns: ColumnModel[] = selectedColumns.filter((column: ColumnModel) => !column.selected);
        const newHiddenColumns = [];
        unselectedColumns.forEach((column) => {
            newHiddenColumns[column.name] = true;
        });
        return newHiddenColumns;
    }

    prevStep(): void {
        this.back.emit();
    }

    nextStep(): void {
        if (!this.disableNextStep()) {
            this.queryContext.environments = Array.from(this.selectedItems.values());
            this.eventManager.broadcast({ name: 'reset-results', content: 'OK' });
            this.next.emit();
        }
    }

    disableNextStep(): boolean {
        return !this.selectedItems || this.selectedItems.size === 0;
    }
}
